import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useOrgHall } from "@/hooks/useOrgHall";
import { useOrganization } from "@/hooks/useOrganization";
import { formatPrice } from "@/utils/formatPrice";
import { useAuth } from "@/hooks/useAuth";
import { useUserRole } from "@/hooks/useUserRole";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { PendingPaymentBadge } from "./PendingPaymentBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import BulkCancelConfirmDialog from "@/components/admin/BulkCancelConfirmDialog";
import { toast } from "sonner";
import { useState } from "react";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { XCircle, CheckCircle, Bell, CheckCircle2, AlertTriangle, Trash2 } from "lucide-react";
import { getLocalDayBounds, parseLocalDate } from "@/lib/utils";
import PaymentStatusBadge from "@/components/booking/PaymentStatusBadge";
import type { Tables } from "@/integrations/supabase/types";

type BookingWithJoins = Tables<"bookings"> & {
  courts: { name: string; hall_id: string } | null;
  profiles: { full_name: string | null } | null;
};

type _OpenMatchWithProfile = {
  id: string;
  activity_title: string | null;
  match_type: string;
  date: string;
  start_time: string;
  created_by: string;
  spots_filled: number | null;
  organizer_fee_per_person: number | null;
  organizer_payout_status: string | null;
  profiles: { full_name: string | null } | null;
};

const statusColors: Record<string, string> = { confirmed: "bg-green-500/10 text-green-700", cancelled: "bg-red-500/10 text-red-700", completed: "bg-blue-500/10 text-blue-700" };

export default function AdminBookings() {
  const { t } = useTranslation("admin");
  const qc = useQueryClient();
  const { user } = useAuth();
  const { hallId, hall } = useOrgHall();
  const { organization } = useOrganization();
  const [dateFilter, setDateFilter] = useState(format(new Date(), "yyyy-MM-dd"));
  const [statusFilter, setStatusFilter] = useState("all");
  const [paymentFilter, setPaymentFilter] = useState("all");
  const [deleteTarget, setDeleteTarget] = useState<BookingWithJoins | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");
  const [cancelTarget, setCancelTarget] = useState<BookingWithJoins | null>(null);
  // PR-AK — Bulk-cancel selection state. Pattern mirrors CustomerManagement.
  const [selectedBookingIds, setSelectedBookingIds] = useState<Set<string>>(new Set());
  const [bulkCancelOpen, setBulkCancelOpen] = useState(false);

  // Use the canonical useUserRole hook instead of a duplicate useQuery.
  // The previous version redeclared ["user-roles", user.id, org.id] but
  // returned { role }[] instead of string[] — polluting the React Query
  // cache and breaking downstream consumers (ManagerRoute, usePermissions).
  const { isAdmin: isOrgAdmin } = useUserRole();

  const { data: bookings = [] } = useQuery({
    queryKey: ["admin-all-bookings", hallId, dateFilter, statusFilter],
    queryFn: async () => {
      const { start, end } = getLocalDayBounds(parseLocalDate(dateFilter));
      let query = supabase
        .from("bookings")
        .select("*, courts!inner(name, hall_id), profiles:bookings_user_id_profiles_fkey(full_name)")
        .eq("courts.hall_id", hallId!)
        .gte("start_time", start)
        .lte("start_time", end)
        .is("deleted_at", null)
        .order("start_time", { ascending: true });

      if (statusFilter === "pending_payment") {
        query = query.eq("payment_status" as any, "pending_payment");
      } else if (statusFilter !== "all") {
        query = query.eq("status", statusFilter as "confirmed" | "cancelled" | "completed");
      }
      const { data, error } = await query;
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!hallId,
  });

  const bookingIds = bookings.map((b) => b.id);
  const { data: payments = [] } = useQuery({
    queryKey: ["admin-booking-payments", bookingIds],
    queryFn: async () => {
      if (bookingIds.length === 0) return [];
      const { data, error } = await supabase.from("booking_payments").select("*").in("booking_id", bookingIds);
      if (error) throw error;
      return data ?? [];
    },
    enabled: bookingIds.length > 0,
  });

  // T1/G-016: the raw status='cancelled' mutation is removed — single-row
  // cancel now goes through the SAME refund-aware BulkCancelConfirmDialog
  // (→ cancelSingleBooking: refund, optional late-cancel fee, activity log,
  // waitlist offer, league cascade, payment_status sync) as bulk cancel.
  // ONE canonical admin-cancel path.

  const markPaidMutation = useMutation({
    mutationFn: async ({ bookingId, userId, amount }: { bookingId: string; userId: string; amount: number }) => {
      const existing = payments.find((p) => p.booking_id === bookingId && p.user_id === userId);
      if (existing) {
        const { error } = await supabase
          .from("booking_payments")
          .update({ status: "manual", payment_method: "manual", paid_at: new Date().toISOString() })
          .eq("id", existing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("booking_payments").insert({
          organization_id: organization!.id, booking_id: bookingId, user_id: userId,
          amount,                                          // legacy kronor
          amount_minor: Math.round(amount * 100),          // öre (Sprint D dual-write)
          currency: "SEK",
          status: "manual", payment_method: "manual", paid_at: new Date().toISOString(),
        });
        if (error) throw error;
      }
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-booking-payments"] }); toast.success(t("bookingsPage.markedAsPaid")); },
    onError: (err: Error) => { console.error(err); toast.error(err?.message || t("bookingsPage.couldNotUpdatePayment")); },
  });

  const sendReminderMutation = useMutation({
    mutationFn: async ({ userId, bookingId }: { userId: string; bookingId: string }) => {
      const { error } = await supabase.from("notifications").insert({
        user_id: userId,
        organization_id: organization!.id,  // PR-A: org-scope per Phase 1 RLS
        type: "payment_reminder",
        title: t("bookingsPage.paymentReminderTitle"), body: t("bookingsPage.paymentReminderBody"),
        data: { booking_id: bookingId },
      });
      if (error) throw error;
    },
    onSuccess: () => toast.success(t("bookingsPage.reminderSent")),
    onError: (err: Error) => { console.error(err); toast.error(err?.message || t("bookingsPage.couldNotSendReminder")); },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!deleteTarget || !deleteReason.trim()) throw new Error(t("bookingsPage.reasonRequiredError"));
      await supabase.from("booking_audit_log").insert({
        booking_id: deleteTarget.id, action: "deleted", performed_by: user!.id,
        reason: deleteReason, booking_snapshot: deleteTarget,
      });
      const { error } = await supabase.from("bookings").update({
        deleted_at: new Date().toISOString(), deleted_by: user!.id, delete_reason: deleteReason,
      }).eq("id", deleteTarget.id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-all-bookings"] });
      setDeleteDialogOpen(false); setDeleteTarget(null); setDeleteReason("");
      toast.success(t("bookingsPage.bookingDeleted"));
    },
    onError: (e: Error) => toast.error(e.message || t("bookingsPage.couldNotDelete")),
  });

  const getPaymentStatus = (bookingId: string) => {
    const bp = payments.filter((p) => p.booking_id === bookingId);
    if (bp.length === 0) return "unpaid";
    return bp[0].status;
  };

  const getPaymentAmount = (bookingId: string) => {
    const bp = payments.filter((p) => p.booking_id === bookingId);
    return bp.reduce((sum: number, p) => sum + (["paid", "manual"].includes(p.status) ? p.amount : 0), 0);
  };

  const filteredBookings = bookings.filter((b) => {
    if (paymentFilter === "all") return true;
    const ps = getPaymentStatus(b.id);
    if (paymentFilter === "unpaid") return ps === "unpaid" || ps === "pending";
    if (paymentFilter === "paid") return ps === "paid" || ps === "manual";
    return true;
  });

  // PR-AK — Multi-select helpers. Only "confirmed" bookings are selectable
  // (cancelled/completed shouldn't be re-cancelled).
  const selectableIds = filteredBookings
    .filter((b) => b.status === "confirmed")
    .map((b) => b.id);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedBookingIds.has(id));
  const someSelected = selectedBookingIds.size > 0;
  const toggleAll = () => {
    if (allSelected) {
      setSelectedBookingIds(new Set());
    } else {
      setSelectedBookingIds(new Set(selectableIds));
    }
  };
  const toggleOne = (id: string) => {
    setSelectedBookingIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  // Drop selection IDs that are no longer in the filtered list (filter changed).
  const validSelectedIds = Array.from(selectedBookingIds).filter((id) =>
    filteredBookings.some((b) => b.id === id),
  );

  return (
    <div className="space-y-3">
      <div className="flex gap-2 flex-wrap">
        <Input type="date" className="flex-1 h-9 min-w-[140px]" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-9 flex-1 min-w-[120px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("bookingsPage.allStatuses")}</SelectItem>
            <SelectItem value="confirmed">{t("bookingsPage.confirmedPlural")}</SelectItem>
            <SelectItem value="cancelled">{t("bookingsPage.cancelledPlural")}</SelectItem>
            <SelectItem value="completed">{t("bookingsPage.completedPlural")}</SelectItem>
            <SelectItem value="pending_payment">{t("bookingsPage.pendingPaymentPlural")}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={paymentFilter} onValueChange={setPaymentFilter}>
          <SelectTrigger className="h-9 flex-1 min-w-[120px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("bookingsPage.allPayments")}</SelectItem>
            <SelectItem value="unpaid">{t("bookingsPage.unpaid")}</SelectItem>
            <SelectItem value="paid">{t("bookingsPage.paid")}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* PR-AK — Bulk select-all bar (only when there are selectable bookings) */}
      {selectableIds.length > 0 && (
        <div className="flex items-center gap-2 px-3 py-2 border-b sticky top-0 bg-background z-10">
          <Checkbox
            id="bulk-select-all"
            checked={allSelected}
            onCheckedChange={toggleAll}
          />
          <Label htmlFor="bulk-select-all" className="text-xs cursor-pointer">
            {allSelected ? t("bookingsPage.bulkDeselectAll", { defaultValue: "Avmarkera alla" }) : t("bookingsPage.bulkSelectAll", { defaultValue: "Markera alla" })}
          </Label>
          {someSelected && (
            <span className="text-xs text-muted-foreground ml-auto">
              {t("bookingsPage.bulkSelectedCount", { defaultValue: "Markerade: {{count}}", count: validSelectedIds.length })}
            </span>
          )}
        </div>
      )}

      {filteredBookings.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8">{t("bookingsPage.noBookingsFound")}</p>
      ) : (
        filteredBookings.map((b) => {
          const paidAmount = getPaymentAmount(b.id);
          const totalAmount = b.price_total ?? 0;
          const isSelectable = b.status === "confirmed";
          const isSelected = selectedBookingIds.has(b.id);
          return (
            <Card key={b.id} className={isSelected ? "ring-2 ring-primary/40" : ""}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2">
                  {/* PR-AK — Per-row checkbox (only confirmed bookings) */}
                  {isSelectable && (
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => toggleOne(b.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="mt-1"
                      aria-label={t("bookingsPage.bulkSelectOne", { defaultValue: "Välj bokning" })}
                    />
                  )}
                  <div className="space-y-1 flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">
                        {format(new Date(b.start_time), "HH:mm", { locale: sv })}–
                        {format(new Date(b.end_time), "HH:mm", { locale: sv })}
                      </span>
                      <Badge className={statusColors[b.status]}>{t(`bookingsPage.statusLabels.${b.status}`)}</Badge>
                      {(b as any).payment_status === "pending_payment" && (b as any).payment_expires_at && (
                        <PendingPaymentBadge expiresAt={(b as any).payment_expires_at} />
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">{b.courts?.name} · {b.duration_minutes} min</p>
                    <p className="text-xs text-muted-foreground">
                      {b.profiles?.full_name || t("common.unknownPlayer")} · PIN: {b.pin_code}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <PaymentStatusBadge status={getPaymentStatus(b.id)} />
                      {totalAmount > 0 && (
                        <span className="text-xs text-muted-foreground">{paidAmount} / {totalAmount} kr</span>
                      )}
                    </div>
                    <div className="mt-1">
                      {b.checked_in_at ? (
                        <span className="text-xs text-green-700 flex items-center gap-1">
                          <CheckCircle2 className="h-3 w-3" />{t("bookingsPage.checkedInAt")} {format(new Date(b.checked_in_at), "HH:mm")}
                        </span>
                      ) : new Date() > new Date(new Date(b.start_time).getTime() + ((hall as Tables<"halls"> | null)?.checkin_closes_minutes ?? 15) * 60000) && b.status === "confirmed" ? (
                        <Badge variant="destructive" className="text-[10px] px-1.5 py-0">
                          <AlertTriangle className="h-2.5 w-2.5 mr-0.5" />{t("bookingsPage.noShow")}
                        </Badge>
                      ) : null}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    {b.status === "confirmed" && (
                      <Button variant="ghost" size="icon" onClick={() => setCancelTarget(b)} title={t("bookingsPage.cancelBooking")}>
                        <XCircle className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                    {!["paid", "manual"].includes(getPaymentStatus(b.id)) && (
                      <>
                        <Button variant="ghost" size="icon" onClick={() => markPaidMutation.mutate({ bookingId: b.id, userId: b.user_id, amount: totalAmount })} title={t("bookingsPage.markAsPaid")}>
                          <CheckCircle className="h-4 w-4 text-green-600" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => sendReminderMutation.mutate({ userId: b.user_id, bookingId: b.id })} title={t("bookingsPage.sendReminder")}>
                          <Bell className="h-4 w-4 text-yellow-600" />
                        </Button>
                      </>
                    )}
                    {isOrgAdmin && (b.status === "cancelled" || b.status === "completed") && (
                      <Button variant="ghost" size="icon" onClick={() => { setDeleteTarget(b); setDeleteDialogOpen(true); }} title={t("bookingsPage.deletePermanently")}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })
      )}

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("bookingsPage.deleteBookingTitle")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {deleteTarget && (
              <p className="text-sm">{t("bookingsPage.bookingLabel")} {deleteTarget.courts?.name} · {format(new Date(deleteTarget.start_time), "d MMM HH:mm", { locale: sv })}</p>
            )}
            <p className="text-xs text-muted-foreground">{t("bookingsPage.deleteWarning")}</p>
            <div className="space-y-1">
              <Label>{t("bookingsPage.reasonRequired")}</Label>
              <Textarea value={deleteReason} onChange={(e) => setDeleteReason(e.target.value)} placeholder={t("bookingsPage.reasonPlaceholder")} className="h-16 text-sm" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>{t("common.cancel")}</Button>
            <Button variant="destructive" disabled={!deleteReason.trim() || deleteMutation.isPending} onClick={() => deleteMutation.mutate()}>{t("bookingsPage.deletePermanently")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* T1/G-016: single-row cancel converged onto the refund-aware dialog
          (same instance shape as the bulk dialog below, with one id). */}
      <BulkCancelConfirmDialog
        open={!!cancelTarget}
        onOpenChange={(open) => { if (!open) setCancelTarget(null); }}
        bookingIds={cancelTarget ? [cancelTarget.id] : []}
        onCompleted={(results) => {
          setCancelTarget(null);
          if (results.every((r) => r.success)) {
            toast.success(t("bookingsPage.bookingCancelled"));
          } else {
            toast.error(t("bookingsPage.couldNotCancel"));
          }
        }}
      />

      {/* Arrangörsutbetalningar */}
      <OrganizerPayoutsSection organizationId={organization?.id} />

      {/* PR-AK — Sticky bottom bulk-action bar (Q6 Option A: full-width mobile) */}
      {validSelectedIds.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-50 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 p-3 shadow-lg">
          <div className="max-w-screen-md mx-auto flex items-center gap-2">
            <span className="text-sm font-medium">
              {t("bookingsPage.bulkSelectedCount", { defaultValue: "Markerade: {{count}}", count: validSelectedIds.length })}
            </span>
            <div className="ml-auto flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedBookingIds(new Set())}
              >
                {t("common.cancel")}
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setBulkCancelOpen(true)}
              >
                <XCircle className="h-4 w-4 mr-1" />
                {t("bookingsPage.bulkCancelButton", { defaultValue: "Avboka markerade ({{count}})", count: validSelectedIds.length })}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* PR-AK — Bulk cancel confirmation dialog */}
      <BulkCancelConfirmDialog
        open={bulkCancelOpen}
        onOpenChange={setBulkCancelOpen}
        bookingIds={validSelectedIds}
        onCompleted={(results) => {
          setSelectedBookingIds(new Set());
          const successes = results.filter((r) => r.success).length;
          const failures = results.length - successes;
          if (failures === 0) {
            toast.success(t("bookingsPage.bulkCancelSuccess", { defaultValue: "Avbokade {{count}} bokningar", count: successes }));
          } else {
            toast.warning(t("bookingsPage.bulkCancelPartial", { defaultValue: "Avbokade {{ok}}/{{total}}. {{failed}} misslyckades.", ok: successes, total: results.length, failed: failures }));
          }
        }}
      />
    </div>
  );
}

// ─── Organizer Payouts Section ───────────────────────────────────
function OrganizerPayoutsSection({ organizationId }: { organizationId: string | undefined }) {
  const { t } = useTranslation("admin");
  const qc = useQueryClient();

  const { data: pendingPayouts = [] } = useQuery({
    queryKey: ["organizer-payouts", organizationId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("activities")
        .select("id, activity_title, match_type, date, start_time, created_by, spots_filled, organizer_fee_per_person, organizer_payout_status, profiles:created_by(full_name)")
        .eq("organization_id", organizationId!)
        .eq("organizer_payout_method", "hall_payout")
        .eq("organizer_payout_status", "pending")
        .eq("status", "full")
        .order("date", { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!organizationId,
  });

  const markPaidMutation = useMutation({
    mutationFn: async (matchId: string) => {
      const { error } = await supabase
        .from("activities")
        .update({ organizer_payout_status: "paid", organizer_payout_at: new Date().toISOString() })
        .eq("id", matchId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["organizer-payouts"] });
      toast.success(t("bookingsPage.markedPaidOut"));
    },
    onError: (err: Error) => { console.error(err); toast.error(err?.message || t("common.couldNotUpdate")); },
  });

  if (pendingPayouts.length === 0) return null;

  return (
    <div className="mt-6 space-y-3">
      <h3 className="text-sm font-semibold">{t("bookingsPage.organizerPayouts")}</h3>
      {pendingPayouts.map((p) => {
        const totalAmount = (p.organizer_fee_per_person ?? 0) * (p.spots_filled ?? 0);
        return (
          <Card key={p.id}>
            <CardContent className="p-4 flex items-center justify-between">
              <div className="space-y-0.5">
                <p className="text-sm font-medium">{p.activity_title || p.match_type}</p>
                <p className="text-xs text-muted-foreground">
                  {p.profiles?.full_name ?? t("common.unknown")} · {p.date} {p.start_time?.slice(0, 5)}
                </p>
                <p className="text-xs font-medium">{formatPrice(totalAmount)} {t("bookingsPage.toPay")}</p>
              </div>
              <Button size="sm" variant="outline" onClick={() => markPaidMutation.mutate(p.id)} disabled={markPaidMutation.isPending}>
                {t("bookingsPage.markPaidOut")}
              </Button>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
